import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface CurrencyExchangeRate {
  rate: number;
  effectiveFrom?: string;
  effectiveTill?: string;
}

interface Country {
  _id: string;
  name: string;
  code: string;
  dialCode?: string;
  countryCode?: string;
  currencyCode?: string;
  currencySymbol?: string;
  currencyName?: string;
  currencyFractionalUnit?: string;
  status: 'Active' | 'Inactive';
  exchangeRates?: CurrencyExchangeRate[];
  createdAt: string;
  updatedAt: string;
}

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const CountryManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch countries with server-side filtering and pagination
  const { data: countriesResponse, isLoading } = useQuery({
    queryKey: ['countries', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/country?${params.toString()}`);
      const result = response.data.data || {};
      const paginationData = result.pagination || {};
      console.log('Country API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: paginationData.total || 0,
          page: paginationData.page || 1,
          limit: paginationData.limit || 50,
          totalPages: paginationData.totalPages || 1
        }
      };
    },
  });

  const countries = countriesResponse?.data || [];
  const pagination = countriesResponse?.pagination;

  // Create country mutation
  const createCountryMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/country', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast({
        title: 'Country created',
        description: 'Country has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingCountry(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error',
          description: error.response?.data?.error?.message || 'Failed to create country',
          variant: 'destructive',
        });
      }
    },
  });

  // Update country mutation
  const updateCountryMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/master-data/country/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast({
        title: 'Country updated',
        description: 'Country has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingCountry(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error',
          description: error.response?.data?.error?.message || 'Failed to update country',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete country mutation
  const deleteCountryMutation = useMutation({
    mutationFn: async (countryId: string) => {
      await axios.delete(`/api/master-data/country/${countryId}?soft=false`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      toast({
        title: 'Country deleted',
        description: 'Country has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete country',
        variant: 'destructive',
      });
    },
  });

  // Import countries mutation
  const importCountriesMutation = useMutation({
    mutationFn: async (records: { code: string; name: string; countryCode: string; status: string }[]) => {
      const response = await axios.post('/api/master-data/country/bulk', { records });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      const count = data?.data?.length || data?.data?.created || 0;
      const message = data?.message || `Successfully imported ${count} countries.`;
      toast({
        title: 'Import successful',
        description: message,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Import failed',
        description: error.response?.data?.error?.message || 'Failed to import countries',
        variant: 'destructive',
      });
    },
  });

  // Table columns with better responsive configuration
  const columns: TableColumn<Country>[] = [
    {
      key: 'name',
      label: 'Country Name',
      sortable: true,
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'countryCode',
      label: 'Country Code',
      sortable: true,
      hideOnTablet: true,
      render: (value) => value || '-',
    },
    {
      key: 'dialCode',
      label: 'Dial Code',
      sortable: true,
      render: (value) => value || '-',
    },
    {
      key: 'currencyCode',
      label: 'Currency Code',
      sortable: true,
      hideOnTablet: true,
      render: (value) => value || '-',
    },
    {
      key: 'currencyName',
      label: 'Currency',
      sortable: true,
      hideOnTablet: true,
      render: (value) => value || '-',
    },
    {
      key: 'currencySymbol',
      label: 'Symbol',
      sortable: true,
      hideOnTablet: true,
      render: (value) => value || '-',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge 
          variant={value === 'Active' ? 'default' : 'secondary'}
          className={value === 'Active' 
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {value}
        </Badge>
      ),
    },
  ];

  // Filters with improved configuration
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Filter by status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'Active', label: 'Active Countries' },
        { value: 'Inactive', label: 'Inactive Countries' },
      ],
    },
    {
      key: 'currencyName',
      label: 'Currency',
      type: 'text',
      placeholder: 'Search by currency name...',
    },
    {
      key: 'countryCode',
      label: 'Country Code',
      type: 'text',
      placeholder: 'Filter by country code...',
    },
  ];

  // Form sections
  const formSections: FormSection[] = [
    {
      title: 'Country Information',
      description: 'Basic country details and identification codes',
      fields: [
        {
          name: 'name',
          label: 'Country Name',
          type: 'text',
          required: true,
          placeholder: 'Enter unique country name',
        },
        {
          name: 'code',
          label: 'Code',
          type: 'text',
          required: false,
          placeholder: 'Auto-generated if left empty',
          description: 'Leave empty for auto-generation or enter custom code',
        },
        {
          name: 'dialCode',
          label: 'Dial Code',
          type: 'text',
          required: false,
          placeholder: 'Enter dial code (e.g., 1 for USA, 91 for India)',
          description: 'Enter numbers only (1-4 digits), + prefix will be added automatically',
        },
        {
          name: 'countryCode',
          label: 'Country Code',
          type: 'text',
          required: false,
          placeholder: 'Enter country code (e.g., US, IN)',
          description: 'ISO country code (1-3 characters)',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
        },
      ],
    },
    {
      title: 'Currency Information',
      description: 'Currency details and exchange rate information',
      fields: [
        {
          name: 'currencyCode',
          label: 'Currency Code',
          type: 'text',
          required: false,
          placeholder: 'Enter currency code (e.g., USD, INR, EUR)',
          description: 'ISO 4217 currency code (up to 3 characters)',
        },
        {
          name: 'currencySymbol',
          label: 'Currency Symbol',
          type: 'text',
          required: false,
          placeholder: 'Enter currency symbol (e.g., $, ₹, €)',
        },
        {
          name: 'currencyName',
          label: 'Currency Name',
          type: 'text',
          required: false,
          placeholder: 'Enter currency name (e.g., Indian Rupee)',
        },
        {
          name: 'currencyFractionalUnit',
          label: 'Currency Fractional Unit',
          type: 'text',
          required: false,
          placeholder: 'Enter fractional unit (e.g., Paisa, Cents)',
        },
      ],
    },
    {
      title: 'Exchange Rate Information',
      description: 'Current exchange rate details (optional)',
      fields: [
        {
          name: 'exchangeRate',
          label: 'Exchange Rate',
          type: 'number',
          required: false,
          min: 0,
          step: 0.0001,
          placeholder: 'Enter exchange rate',
        },
        {
          name: 'effectiveFrom',
          label: 'Effective From',
          type: 'date',
          placeholder: 'Select effective from date',
        },
        {
          name: 'effectiveTill',
          label: 'Effective Till',
          type: 'date',
          placeholder: 'Select effective till date',
        },
      ],
    },
  ];

  const handleAdd = () => {
    setEditingCountry(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (country: Country) => {
    if (window.confirm(`Are you sure you want to delete country "${country.name}"?`)) {
      deleteCountryMutation.mutate(country._id);
    }
  };

  const handleFormSubmit = (data: any) => {
    // Transform form data to include exchange rate information
    const transformedData = {
      ...data,
      // Add + prefix to dial code if provided and not already present
      dialCode: data.dialCode && data.dialCode.trim() 
        ? (data.dialCode.startsWith('+') ? data.dialCode : `+${data.dialCode}`)
        : undefined,
      // Handle optional fields - set to undefined if empty
      countryCode: data.countryCode?.trim() || undefined,
      currencyCode: data.currencyCode?.trim() || undefined,
      currencySymbol: data.currencySymbol?.trim() || undefined,
      currencyName: data.currencyName?.trim() || undefined,
      currencyFractionalUnit: data.currencyFractionalUnit?.trim() || undefined,
      exchangeRates: data.exchangeRate ? [{
        rate: Number(data.exchangeRate),
        effectiveFrom: data.effectiveFrom ? new Date(data.effectiveFrom) : undefined,
        effectiveTill: data.effectiveTill ? new Date(data.effectiveTill) : undefined,
      }] : [],
    };

    // Remove the flattened exchange rate fields
    delete transformedData.exchangeRate;
    delete transformedData.effectiveFrom;
    delete transformedData.effectiveTill;

    if (editingCountry) {
      updateCountryMutation.mutate({ ...transformedData, _id: editingCountry._id });
    } else {
      createCountryMutation.mutate(transformedData);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['countries'] });
  };

  // Handle CSV file import
  const handleImport = async (file: File) => {
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: 'Invalid file',
          description: 'CSV file must have a header row and at least one data row.',
          variant: 'destructive',
        });
        return;
      }

      // Parse header row (case-insensitive)
      const headerLine = lines[0];
      const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/["']/g, ''));
      
      const codeIndex = headers.findIndex(h => h === 'code');
      const nameIndex = headers.findIndex(h => h === 'name');

      if (codeIndex === -1 || nameIndex === -1) {
        toast({
          title: 'Invalid CSV format',
          description: 'CSV must have CODE and NAME columns.',
          variant: 'destructive',
        });
        return;
      }

      // Parse data rows
      const records: { code: string; name: string; countryCode: string; status: string }[] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Handle CSV with quoted values
        const values: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (const char of line) {
          if (char === '"' || char === "'") {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const code = values[codeIndex]?.replace(/["']/g, '').trim();
        const name = values[nameIndex]?.replace(/["']/g, '').trim();

        if (code && name) {
          records.push({
            code: code.toUpperCase(),
            name,
            countryCode: code.toUpperCase(), // Set countryCode same as code to avoid null duplicate key errors
            status: 'Active',
          });
        }
      }

      if (records.length === 0) {
        toast({
          title: 'No valid records',
          description: 'No valid country records found in the CSV file.',
          variant: 'destructive',
        });
        return;
      }

      // Import the records
      importCountriesMutation.mutate(records);
    } catch (error: any) {
      toast({
        title: 'Import failed',
        description: error.message || 'Failed to parse CSV file',
        variant: 'destructive',
      });
    }
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/country/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Dial Code', 'Country Code', 'Currency Code', 'Currency Symbol', 'Currency Name', 'Currency Fractional Unit', 'Status', 'Created Date'];
          const csvContent = [
            headers.join(','),
            ...data.map((country: any) => [
              `"${country.name || ''}"`,
              `"${country.code || ''}"`,
              `"${country.dialCode || ''}"`,
              `"${country.countryCode || ''}"`,
              `"${country.currencyCode || ''}"`,
              `"${country.currencySymbol || ''}"`,
              `"${country.currencyName || ''}"`,
              `"${country.currencyFractionalUnit || ''}"`,
              `"${country.status || ''}"`,
              `"${new Date(country.createdAt).toLocaleDateString()}"`
            ].join(','))
          ].join('\n');

          // Download CSV
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `countries_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} country records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No countries match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export countries',
        variant: 'destructive',
      });
    }
  };

  // Transform country data for form (flatten exchange rate data)
  const transformedEditingCountry = editingCountry ? {
    ...editingCountry,
    // Remove + prefix from dial code for editing
    dialCode: editingCountry.dialCode?.startsWith('+') ? editingCountry.dialCode.substring(1) : (editingCountry.dialCode || ''),
    countryCode: editingCountry.countryCode || '',
    currencyCode: editingCountry.currencyCode || '',
    currencySymbol: editingCountry.currencySymbol || '',
    currencyName: editingCountry.currencyName || '',
    currencyFractionalUnit: editingCountry.currencyFractionalUnit || '',
    // Get the latest exchange rate for editing
    exchangeRate: editingCountry.exchangeRates?.[0]?.rate || '',
    effectiveFrom: editingCountry.exchangeRates?.[0]?.effectiveFrom ? 
      new Date(editingCountry.exchangeRates[0].effectiveFrom).toISOString().split('T')[0] : '',
    effectiveTill: editingCountry.exchangeRates?.[0]?.effectiveTill ? 
      new Date(editingCountry.exchangeRates[0].effectiveTill).toISOString().split('T')[0] : '',
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="Country Management"
          description="Manage countries, currency information, and exchange rates"
          data={countries}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          onImport={handleImport}
          filters={filters}
          searchPlaceholder="Search countries by name, code, currency, or dial code..."
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          title={editingCountry ? 'Edit Country' : 'Add New Country'}
          description={
            editingCountry
              ? 'Update country information, currency details, and exchange rates.'
              : 'Create a new country with currency information and exchange rates.'
          }
          sections={formSections}
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingCountry(null);
            setServerErrors({});
          }}
          onSubmit={handleFormSubmit}
          isLoading={createCountryMutation.isPending || updateCountryMutation.isPending}
          initialData={transformedEditingCountry}
          mode={editingCountry ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="Country"
        />
      </div>
    </div>
  );
};

export default CountryManagement; 